import BasePage from "components/basePage/BasePage";
import React, { useEffect, useState } from "react";
import {
  getMigrationGroupReport,
  getMigrationsGroups,
} from "services/requests";
import "./ContentMigration.scss";
import moment from "moment";
import ErrorMessage from "components/errorMessage/ErrorMessage";
import GroupFilters from "components/groupFilters/GroupFilters";
import { googleSearchConsoleDataTypes } from "constants/chartDataTypes";
import ChartsContainer from "components/chartsContainer/ChartsContainer";
import {
  buildArrayOfDates,
  buildFiltersURL,
  createChartSubtitles,
  formatDate,
  formatNumber,
  getParameterFromURL,
} from "services/helper";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import { useNavigate } from "react-router-dom";
import Button from "components/button/Button";

function ContentMigration() {
  //STATE
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [clicksChartData, setClickChardData] = useState([]);
  const [impressionsChartData, setImpressionsChardData] = useState([]);
  const [CRTChartData, setCRTChardData] = useState([]);
  const [positionChartData, setPositionChardData] = useState([]);
  const [periodType, setPeriodType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [chartType, setChartType] = useState({
    clicks: "AreaChart",
    impressions: "AreaChart",
  });
  //NAVIGATION
  const navigate = useNavigate();
  //OTHERS
  const isMonth = periodType === "MONTH";
  const chartOptions = { colors: ["#ffc500", "#612a92"] };

  useEffect(() => {
    getMigrationsGroups()
      .then((data) => {
        getGroupFromURL(data);
        setGroups(data);
      })
      .finally(() => {
        setLoading(false);
        setGroupsLoading(false);
      });
  }, []);

  function getGroupFromURL(groupsData) {
    const groupId = getParameterFromURL("group");
    const foundGroup = groupsData?.find((g) => g?.id === parseInt(groupId));
    if (foundGroup) setSelectedGroup(foundGroup);
  }

  function formatChartData(data) {
    let ZOOM = data?.ZOOM || [];
    let BUSCAPE = data?.BUSCAPE || [];
    const noDataFound = BUSCAPE?.length === 0 && ZOOM?.length === 0;

    const dates = buildArrayOfDates(
      findOldestItem(ZOOM, BUSCAPE),
      findNewestItem(ZOOM, BUSCAPE),
      periodType
    );

    if (noDataFound) {
      setError("Nenhum dado encontrado.");
      setClickChardData([]);
      setImpressionsChardData([]);
      setCRTChardData([]);
      setPositionChardData([]);
    } else buildChartsData(dates, BUSCAPE, ZOOM);
  }

  function findOldestItem(ZOOM, BUSCAPE) {
    const zoomOldestDate = createDate(ZOOM[0]);
    const buscapeOldestDate = createDate(BUSCAPE[0]);
    const isBuscapeBefore = moment(buscapeOldestDate).isBefore(zoomOldestDate);
    const isZoomBefore = moment(zoomOldestDate).isBefore(buscapeOldestDate);

    if (!isBuscapeBefore && !isZoomBefore)
      return buscapeOldestDate; //It can be any of them
    else if (isBuscapeBefore) return buscapeOldestDate;
    else return zoomOldestDate;
  }

  function findNewestItem(ZOOM, BUSCAPE) {
    const zoomNewestDate = createDate(ZOOM[ZOOM?.length - 1]);
    const buscapeNewestDate = createDate(BUSCAPE[BUSCAPE?.length - 1]);
    const isBuscapeAfter = moment(buscapeNewestDate).isAfter(zoomNewestDate);
    const isZoomAfter = moment(zoomNewestDate).isAfter(buscapeNewestDate);

    if (!isBuscapeAfter && !isZoomAfter)
      return buscapeNewestDate; //It can be any of them
    else if (isBuscapeAfter) return buscapeNewestDate;
    else return zoomNewestDate;
  }

  function createDate(item) {
    const exp = /-/g;
    const foundDate = isMonth ? item?.month : item?.date;
    return new Date(foundDate.replace(exp, ","));
  }

  function buildChartsData(dates, BUSCAPE, ZOOM) {
    let legends = createChartSubtitles(
      googleSearchConsoleDataTypes,
      buildChartLabels
    );
    dates?.forEach((date) => {
      const line = generateChartLine(date, BUSCAPE, ZOOM);
      legends?.clicks?.push(line[0]);
      legends?.impressions?.push(line[1]);
      legends?.ctr?.push(line[2]);
      legends?.position?.push(line[3]);
    });
    setClickChardData(legends?.clicks);
    setImpressionsChardData(legends?.impressions);
    setCRTChardData(legends?.ctr);
    setPositionChardData(legends?.position);
    setError("");
  }

  function generateChartLine(date, BUSCAPE, ZOOM) {
    const buscapeItem = BUSCAPE?.find(
      (item) => item?.month === date || item?.date === date
    );
    const zoomItem = ZOOM?.find(
      (item) => item?.month === date || item?.date === date
    );
    return googleSearchConsoleDataTypes?.map((type) => {
      const tooltip = createTooltip(
        formatDate(isMonth, date, true),
        buscapeItem ? buscapeItem[type] : 0,
        zoomItem ? zoomItem[type] : 0,
        type
      );

      return [
        moment(date).format(isMonth ? "MM/YY" : "DD/MM"),
        buscapeItem ? buscapeItem[type] : 0,
        tooltip,
        zoomItem ? zoomItem[type] : 0,
        tooltip,
      ];
    });
  }

  function buildChartLabels() {
    return [
      isMonth ? "Month" : "Day",
      "Buscapé",
      { role: "tooltip", type: "string", p: { html: true } },
      "Zoom",
      { role: "tooltip", type: "string", p: { html: true } },
    ];
  }

  function createTooltip(date, buscapeValue, zoomValue, type) {
    const isClickOrImpression = type === "clicks" || type === "impressions";
    const textStyle = "font-size: 16px;";
    const dotStyle =
      "width: 10px; height: 10px; margin: 5px; border-radius: 50px;";
    const total = formatNumber((zoomValue + buscapeValue)?.toFixed(0));
    const ret = formatValues(type, buscapeValue, zoomValue);
    zoomValue = ret?.zoomValue;
    buscapeValue = ret?.buscapeValue;

    return `
      <div style="padding: 5px;">
        <p style="${textStyle}">${date}</p>
        <div style="${textStyle}display: flex;">
          <div style="${dotStyle}background-color: #ffc500; border: 1px solid black;"></div>
          Buscapé: ${buscapeValue}
        </div>
        <div style="${textStyle}display: flex;">
          <div style="${dotStyle}background-color: #612a92; border: 1px solid black"></div>
          Zoom: ${zoomValue}
        </div>
        ${
          isClickOrImpression
            ? `<p style="${textStyle}font-weight: 700;">Total: ${total}</p>`
            : ""
        }
      </div>`;
  }

  function formatValues(type, buscapeValue, zoomValue) {
    const isClickOrImpression = type === "clicks" || type === "impressions";
    const isCTR = type === "ctr";
    const isPosition = type === "position";
    const toFixed = isClickOrImpression ? 0 : 3;
    zoomValue = zoomValue?.toFixed(toFixed);
    buscapeValue = buscapeValue?.toFixed(toFixed);

    if (isClickOrImpression) {
      return {
        zoomValue: formatNumber(zoomValue),
        buscapeValue: formatNumber(buscapeValue),
      };
    } else if (isCTR) {
      return {
        zoomValue: `${(zoomValue * 100).toFixed(1)}%`,
        buscapeValue: `${(buscapeValue * 100).toFixed(1)}%`,
      };
    } else if (isPosition) {
      return {
        zoomValue: parseFloat(zoomValue).toFixed(1),
        buscapeValue: parseFloat(buscapeValue).toFixed(1),
      };
    }
  }

  function findGroupById(groupId) {
    const foundGroup = groups?.find((g) => g?.id === parseInt(groupId));
    setSelectedGroup(foundGroup);
  }

  function renderGroups() {
    return groups?.map((group) => {
      const { id, description } = { ...group };
      return (
        <option value={id} key={id}>
          {description}
        </option>
      );
    });
  }

  function triggerSearch() {
    if (selectedGroup) {
      setLoading(true);
      let _startDate = moment(startDate).format("YYYY-MM-DD");
      let _endDate = moment(endDate).format("YYYY-MM-DD");

      getMigrationGroupReport(
        selectedGroup?.id?.toString(),
        periodType,
        _startDate,
        _endDate
      )
        .then((data) => formatChartData(data))
        .catch(() =>
          setError(
            "Não foi possível carregar os dados, tente novamente mais tarde."
          )
        )
        .finally(() => {
          let url = buildFiltersURL(periodType, _startDate, _endDate);
          navigate(url.concat(`group=${selectedGroup?.id}&`));
          setLoading(false);
        });
    }
  }

  function onChartTypeChange(event, type) {
    let updatedChartType = { ...chartType };
    updatedChartType[type] = event?.target?.value;
    setChartType(updatedChartType);
  }

  return (
    <BasePage
      loading={loading}
      className="content-migration-page"
      title="Migração de Conteúdo"
    >
      <GroupFilters
        onChangePeriod={(newPeriod) => setPeriodType(newPeriod)}
        onStartDateChange={(newStartDate) => setStartDate(newStartDate)}
        onEndDateChange={(newEndDate) => setEndDate(newEndDate)}
      />

      {!groupsLoading && (
        <div className="select-group-container">
          <FormControl className="form">
            <InputLabel>Grupo</InputLabel>
            <Select
              native
              id="grouped-native-select"
              label="Grupo"
              onChange={(event) => findGroupById(event.target.value)}
              size="small"
              value={selectedGroup?.id || ""}
              className="group-selector"
            >
              <option aria-label="None" value="" />
              {renderGroups()}
            </Select>
            <Button
              onClick={triggerSearch}
              className="trigger-search-button"
              text="Buscar"
            />
          </FormControl>
        </div>
      )}

      {!loading && <ErrorMessage message={error} />}
      <ChartsContainer
        visible={!loading && selectedGroup && error?.length === 0}
        selectValue={chartType}
        onChartTypeChange={onChartTypeChange}
        config={[
          {
            chartType: chartType.clicks,
            data: clicksChartData,
            dataType: "clicks",
            options: { ...chartOptions, isStacked: true },
          },
          {
            chartType: chartType.impressions,
            data: impressionsChartData,
            dataType: "impressions",
            options: { ...chartOptions, isStacked: true },
          },
          {
            chartType: "LineChart",
            data: CRTChartData,
            dataType: "ctr",
            options: chartOptions,
          },
          {
            chartType: "LineChart",
            data: positionChartData,
            dataType: "position",
            options: chartOptions,
          },
        ]}
      />
    </BasePage>
  );
}

export default ContentMigration;
